import { EPlatform } from '@enums/campaign.enum';
import { TCampaignApi } from '@pages/campaigns/campaigns.interface';
import { ICreator } from '@pages/creators/creators.interface';
import { camelCase } from 'lodash';
import {
  ICampaignFilterOption,
  ICreatorPickerFilters,
  ICreatorPickerLayout,
  ICreatorPickerLayoutInput,
  ICreatorPickerRow,
  TSelectAllState,
} from './creator-and-budget-dialog.interface';

export const CREATOR_PICKER_LIST_ROW_HEIGHT = 50;
export const CREATOR_PICKER_TRAY_ROW_HEIGHT = 60;

const LIST_MIN_HEIGHT = 4 * CREATOR_PICKER_LIST_ROW_HEIGHT;
const LIST_EMPTY_HEIGHT = 2 * CREATOR_PICKER_LIST_ROW_HEIGHT;
const TRAY_HEIGHT_RATIO = 0.4;

export function getCreatorPlatforms(creator: ICreator): EPlatform[] {
  const creatorPlatforms = new Set(creator.channels?.map((channel) => camelCase(channel.platform) as EPlatform));

  return Object.values(EPlatform).filter((platform) => creatorPlatforms.has(platform));
}

export function getCreatorSearchText(creator: ICreator): string {
  return [creator.name, ...(creator.channels?.map((channel) => channel.platformCreatorId) ?? [])]
    .filter(Boolean)
    .join('\n')
    .toLowerCase();
}

export function toCreatorPickerRow(creator: ICreator, existingCreatorIds: Set<string>): ICreatorPickerRow {
  return {
    alreadyAdded: existingCreatorIds.has(creator.id),
    creator,
    platforms: getCreatorPlatforms(creator),
    searchText: getCreatorSearchText(creator),
  };
}

export function filterCreatorRows(rows: ICreatorPickerRow[], filters: ICreatorPickerFilters): ICreatorPickerRow[] {
  const term = filters.term.trim().toLowerCase();

  return rows.filter(
    (row) =>
      (!filters.hideAdded || !row.alreadyAdded) &&
      (!term || row.searchText.includes(term)) &&
      (!filters.platforms.length || filters.platforms.some((platform) => row.platforms.includes(platform))) &&
      (!filters.campaignCreatorIds || filters.campaignCreatorIds.has(row.creator.id)),
  );
}

export function getSelectableRows(rows: ICreatorPickerRow[]): ICreatorPickerRow[] {
  return rows.filter((row) => !row.alreadyAdded);
}

export function getSelectAllState(rows: ICreatorPickerRow[], selectedIds: Set<string>): TSelectAllState {
  const selectableRows = getSelectableRows(rows);
  const selectedCount = selectableRows.filter((row) => selectedIds.has(row.creator.id)).length;

  if (!selectedCount) {
    return 'none';
  }

  return selectedCount === selectableRows.length ? 'all' : 'some';
}

export function getRangeRows(
  rows: ICreatorPickerRow[],
  anchorCreatorId: string | null,
  targetCreatorId: string,
): ICreatorPickerRow[] | null {
  const anchorIndex = rows.findIndex((row) => row.creator.id === anchorCreatorId);
  const targetIndex = rows.findIndex((row) => row.creator.id === targetCreatorId);

  if (anchorIndex === -1 || targetIndex === -1) {
    return null;
  }

  return getSelectableRows(rows.slice(Math.min(anchorIndex, targetIndex), Math.max(anchorIndex, targetIndex) + 1));
}

export function countPayoutsToOverwrite(costs: (string | null)[], newCost: string): number {
  return costs.filter((cost) => cost !== null && cost !== '' && Number(cost) !== Number(newCost)).length;
}

export function getCampaignFilterOptions(
  campaigns: TCampaignApi[],
  currentCampaignId: string | null,
): ICampaignFilterOption[] {
  return campaigns.reduce((options, campaign) => {
    const creatorIds = (campaign.campaignCreators ?? []).map((campaignCreator) => campaignCreator.creatorId);

    if (campaign.id !== currentCampaignId && creatorIds.length) {
      options.push({ creatorIds, id: campaign.id, name: campaign.name });
    }

    return options;
  }, [] as ICampaignFilterOption[]);
}

// When content overflows, the height is snapped so the last visible row is cut in half - a scroll cue
// that still works when macOS hides scrollbars.
export function getSnappedHeight(availableHeight: number, contentHeight: number, rowHeight: number): number {
  if (contentHeight <= availableHeight) {
    return contentHeight;
  }

  const halfRow = rowHeight / 2;

  return Math.max(halfRow, Math.floor((availableHeight - halfRow) / rowHeight) * rowHeight + halfRow);
}

export function getCreatorPickerLayout(input: ICreatorPickerLayoutInput): ICreatorPickerLayout {
  const { availableHeight, listRowCount, trayCollapsed, trayRowCount } = input;
  let trayRowsHeight = 0;

  if (!trayCollapsed && trayRowCount > 0) {
    const trayCap = Math.max(
      Math.min(availableHeight * TRAY_HEIGHT_RATIO, availableHeight - LIST_MIN_HEIGHT),
      1.5 * CREATOR_PICKER_TRAY_ROW_HEIGHT,
    );

    trayRowsHeight = getSnappedHeight(
      trayCap,
      trayRowCount * CREATOR_PICKER_TRAY_ROW_HEIGHT,
      CREATOR_PICKER_TRAY_ROW_HEIGHT,
    );
  }

  const listAvailableHeight = Math.max(availableHeight - trayRowsHeight, 1.5 * CREATOR_PICKER_LIST_ROW_HEIGHT);
  const listContentHeight =
    listRowCount === null
      ? Number.POSITIVE_INFINITY
      : Math.max(listRowCount * CREATOR_PICKER_LIST_ROW_HEIGHT, LIST_EMPTY_HEIGHT);

  return {
    listHeight: getSnappedHeight(listAvailableHeight, listContentHeight, CREATOR_PICKER_LIST_ROW_HEIGHT),
    trayRowsHeight,
  };
}
